import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu, LogOut, User, Settings, Command, Bell, Sun, Moon, BarChart3 } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useTheme } from '../../contexts/ThemeContext';
import { useSidebar } from '../../contexts/SidebarContext';
import Dropdown from '../ui/Dropdown';
import { cn } from '../../lib/utils';
import { paletteShortcutLabel } from '../../utils/shortcut';
import { useIsAdmin } from '../../hooks/useIsAdmin';

const Header: React.FC = () => {
  const { user, logout } = useAuth();
  const { isDark, setTheme } = useTheme();
  const { collapsed, setMobileOpen } = useSidebar();
  const navigate = useNavigate();
  // QA Q48: admins had no way into /admin from the account menu.
  const isAdmin = useIsAdmin(user?.uid);

  const userMenuItems = [
    { label: 'Profile', icon: User, onClick: () => navigate('/profile') },
    { label: 'Settings', icon: Settings, onClick: () => navigate('/settings') },
    ...(isAdmin ? [{ label: 'Admin', icon: BarChart3, onClick: () => navigate('/admin') }] : []),
    { label: 'Sign out', icon: LogOut, onClick: logout, danger: true, divider: true },
  ];

  return (
    <header
      className={`fixed top-0 right-0 left-0 z-40 h-14 transition-all duration-300 ${
        collapsed ? 'lg:left-[72px]' : 'lg:left-64'
      }`}
    >
      {/* The chambers palette is fixed-light on the redesigned dashboard, but the
          Header is global chrome and hosts the app-wide theme toggle — so it must
          follow dark mode too, or dark inner pages (e.g. /clauses) sit under a
          light header. Light state is the chambers default; dark: variants flip
          it to the chamber surface. */}
      <div className="h-full bg-parchment-card border-b border-parchment-border dark:bg-chamber-panel dark:border-chamber-border">
        {/* Symmetric flex layout — invisible left spacer matches the right
            cluster's width so the centered search lands at true viewport center.
            This is the same trick GitHub / Linear / Notion use. */}
        {/* QA Q12: at phone widths (≈388px) the two fixed 120px clusters plus
            padding pushed the bell and account menu off-screen. Below 480px the
            min-widths drop, padding/gaps tighten and the search collapses to an
            icon, so every control stays on screen. */}
        <div className="h-full pl-2 pr-2 gap-1 min-[480px]:pl-6 min-[480px]:pr-4 min-[480px]:gap-3 flex items-center">
          {/* LEFT — invisible spacer (desktop) or hamburger (mobile). Width
              matches the right cluster: 3 buttons × 36px + 3 gaps × 4px ≈ 120px */}
          <div className="flex items-center justify-start shrink-0 min-[480px]:min-w-[120px]" data-testid="header-left">
            <button
              onClick={() => setMobileOpen(true)}
              className="lg:hidden h-9 w-9 inline-flex items-center justify-center rounded-lg text-parchment-muted-warm hover:text-parchment-muted hover:bg-parchment-hover dark:text-chamber-muted dark:hover:text-parchment dark:hover:bg-white/[0.06]"
              aria-label="Open navigation"
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>

          {/* CENTER — search trigger, takes all remaining space, capped at max-w-2xl */}
          <div className="flex-1 min-w-0 flex justify-end min-[480px]:justify-center">
            <button
              onClick={() => document.dispatchEvent(new CustomEvent('open-command-palette'))}
              className={cn(
                'group h-9 w-9 justify-center min-[480px]:w-full min-[480px]:max-w-2xl min-[480px]:justify-start flex items-center gap-3 px-0 min-[480px]:px-3 rounded-lg text-sm transition-colors font-instrument',
                'bg-parchment border border-parchment-border',
                'text-parchment-muted hover:border-parchment-border-strong',
                'dark:bg-chamber-card dark:border-chamber-border dark:text-chamber-muted dark:hover:border-chamber-border-hi',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-brass focus-visible:ring-offset-1 focus-visible:ring-offset-parchment-card dark:focus-visible:ring-offset-chamber-panel',
              )}
              aria-label="Open search"
            >
              <Command className="h-4 w-4 shrink-0" />
              <span className="hidden min-[480px]:inline flex-1 text-left truncate">Search anything…</span>
              <kbd className="hidden md:inline-flex items-center px-1.5 h-5 text-[10px] font-plex font-medium bg-parchment-card border border-parchment-border rounded dark:bg-chamber-panel dark:border-chamber-border">
                {paletteShortcutLabel()}
              </kbd>
            </button>
          </div>

          {/* RIGHT cluster — width should match left spacer (~120px) */}
          <div className="flex items-center gap-0.5 min-[480px]:gap-1 justify-end shrink-0 min-[480px]:min-w-[120px]" data-testid="header-right">
            {/* Theme toggle */}
            <button
              onClick={() => setTheme(isDark ? 'light' : 'dark')}
              className="h-9 w-9 inline-flex items-center justify-center rounded-lg text-parchment-muted-warm hover:text-parchment-muted hover:bg-parchment-hover dark:text-chamber-muted dark:hover:text-parchment dark:hover:bg-white/[0.06] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brass focus-visible:ring-offset-1 focus-visible:ring-offset-parchment-card dark:focus-visible:ring-offset-chamber-panel"
              aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {isDark ? <Sun className="h-[18px] w-[18px]" /> : <Moon className="h-[18px] w-[18px]" />}
            </button>

            {/* Notification bell */}
            <button
              className="h-9 w-9 inline-flex items-center justify-center rounded-lg text-parchment-muted-warm hover:text-parchment-muted hover:bg-parchment-hover dark:text-chamber-muted dark:hover:text-parchment dark:hover:bg-white/[0.06] transition-colors relative focus:outline-none focus-visible:ring-2 focus-visible:ring-brass focus-visible:ring-offset-1 focus-visible:ring-offset-parchment-card dark:focus-visible:ring-offset-chamber-panel"
              aria-label="Notifications"
            >
              <Bell className="h-[18px] w-[18px]" />
            </button>

            {/* User menu — avatar wrapped in an h-9 button so it shares the baseline */}
            {user && (
              <Dropdown
                trigger={
                  <button
                    className="h-9 w-9 inline-flex items-center justify-center rounded-full hover:bg-parchment-hover dark:hover:bg-white/[0.06] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brass focus-visible:ring-offset-1 focus-visible:ring-offset-parchment-card dark:focus-visible:ring-offset-chamber-panel ml-1"
                    aria-label="Open account menu"
                  >
                    <span className="w-8 h-8 inline-flex items-center justify-center rounded-full text-sm font-display ring-2 bg-brass text-chamber-ink ring-brass-border">
                      {(user.displayName || user.email || 'U').charAt(0).toUpperCase()}
                    </span>
                  </button>
                }
                items={userMenuItems}
              />
            )}
          </div>
        </div>
      </div>
      {/* Subtle accent line */}
      <div className="h-[2px] bg-brass-hairline" />
    </header>
  );
};

export default Header;
